import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, MapPin, Clock, Send } from 'lucide-react'
import { supabase } from '../lib/supabase'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import SEO from '../components/SEO'

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'hello@petitepalate.com.au',
    href: 'mailto:hello@petitepalate.com.au',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Melbourne, Australia',
  },
  {
    icon: Clock,
    label: 'Response Time',
    value: 'Within 24 hours',
  },
]

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const { error } = await supabase
        .from('contact_enquiries')
        .insert({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        })

      if (error) throw error

      setIsSubmitted(true)
      setFormData({ name: '', email: '', subject: '', message: '' })
    } catch (error) {
      console.error('Error submitting contact form:', error)
      alert('Something went wrong. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-pp-ivory">
      <SEO
        title="Contact Us"
        description="Get in touch with Petite Palate. We'd love to hear from you about our sensory teethers or answer any questions."
        url="/contact"
      />
      {/* Hero */}
      <section className="bg-pp-linen py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display text-display-lg text-pp-ink mb-6">
              Get in Touch
            </h1>
            <p className="text-pp-stone text-lg leading-relaxed">
              Have a question, suggestion, or just want to say hello? We'd love to
              hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-display text-xl text-pp-ink mb-6">
                Contact Information
              </h2>

              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-pp-sage-soft/50 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-pp-sage" />
                    </div>
                    <div>
                      <p className="text-sm text-pp-stone">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-pp-ink hover:text-pp-terracotta transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-pp-ink">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12">
                <h3 className="font-display text-lg text-pp-ink mb-4">
                  Common Questions
                </h3>
                <p className="text-pp-stone text-sm leading-relaxed">
                  Before reaching out, you might find your answer in our{' '}
                  <a href="/faq" className="text-pp-terracotta hover:underline">
                    FAQ section
                  </a>
                  . We've covered topics like shipping, returns, product care, and
                  more.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              {isSubmitted ? (
                <div className="bg-white rounded-2xl p-12 border border-pp-sand/50 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-pp-sage-soft flex items-center justify-center">
                    <Send className="w-8 h-8 text-pp-sage" />
                  </div>
                  <h3 className="font-display text-xl text-pp-ink mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-pp-stone">
                    Thank you for reaching out. We'll get back to you within 24
                    hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-6 text-pp-terracotta hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-2xl p-6 lg:p-8 border border-pp-sand/50"
                >
                  <h2 className="font-display text-xl text-pp-ink mb-6">
                    Send us a Message
                  </h2>

                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                      <Input
                        label="Email Address"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <Input
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />

                    <div>
                      <label className="block text-sm font-medium text-pp-charcoal mb-2">
                        Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        required
                        className="w-full px-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink placeholder-pp-stone/60 focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 focus:border-pp-terracotta transition-colors resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full mt-6"
                    isLoading={isSubmitting}
                  >
                    <Send className="w-4 h-4" />
                    Send Message
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
